import React, {
  forwardRef,
} from 'react';
import type {
  ForwardedRef,
  MouseEvent,
  ReactNode,
  KeyboardEvent,
} from 'react';
import clsx from 'clsx';
import MuiButton from '@mui/material/Button';

export type Props = {
  className?: string;
  type?: 'button' | 'submit';
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'inherit' | 'primary' | 'secondary' | 'error';
  size?: 'small' | 'medium' | 'large';
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  onMouseDown?: (event: MouseEvent<HTMLButtonElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  href?: string;
  children?: ReactNode;
  endIcon?: ReactNode;
  startIcon?: ReactNode;
  disableElevation?: boolean;
  disableRipple?: boolean;
  tabIndex?: number;
};

const buttonClasses: Record<string, string> = {
  text: 'button-text',
  outlined: 'button-outlined',
  contained: 'button-contained',
};
const buttonColors: Record<string, 'primary' | 'secondary' | 'error'> = {
  primary: 'secondary',
  secondary: 'primary',
  error: 'error',
};

const Button = forwardRef(({
  className,
  type = 'button',
  variant = 'contained',
  color = 'inherit',
  size,
  onClick,
  onFocus,
  onBlur,
  onKeyDown,
  disabled,
  href,
  children,
  endIcon,
  startIcon,
  disableElevation = false,
  disableRipple = false,
  ...props
}: Props, ref: ForwardedRef<HTMLButtonElement>) => {
  const classes = clsx(
    'button',
    className,
    buttonClasses[variant],
  );

  return (
    <MuiButton
      className={classes}
      type={type}
      variant={variant}
      color={buttonColors[color] || 'inherit'}
      size={size}
      onClick={onClick}
      disabled={disabled}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      href={href}
      ref={ref}
      endIcon={endIcon}
      startIcon={startIcon}
      disableElevation={disableElevation}
      disableRipple={disableRipple}
      {...props}
    >
      {children}
    </MuiButton>
  );
});

export { Button };
export default Button;
